import CloseIcon from "@mui/icons-material/Close";
import NotInterestedIcon from "@mui/icons-material/NotInterested";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import {
  ConfirmResponse,
  useConfirmDialog
} from "_shared/_providers/dialogs/confirm/confirm-dialog";
import Guid from "_shared/guids/guid";
import { useSnackbar } from "notistack";
import React from "react";
import { useSession } from "users/__session/session-context";
import { MenuButtonRef, MenuButton } from "_shared/_views/menus/menu-button";
import NetworkInvitationAPIRequest from "users/networking/connections/invitations/_api/commands/network-invitation-api-command";
import NetworkInvitationInfo from "users/networking/connections/invitations/network-invitation-info";
import UserNetworkConnection from "users/networking/connections/user-network-connection";
import { useRemoveNetworkConnectionMutation } from "users/networking/connections/_api/user-network-connection-api";
import { useSelector } from "react-redux";
import { 
  selectIsNetworkedWith, 
  selectNetworkConnectionByUserId 
} from "users/networking/connections/_selectors/user-network-connection-selectors";
import { 
  useAcceptNetworkInvitationMutation, 
  useCancelNetworkInvitationMutation, 
  useCreateNetworkInvitationMutation, 
  useGetUserNetworkInvitationsQuery,
  useRejectNetworkInvitationMutation 
} from "users/networking/connections/invitations/_api/network-invitation-api";
import NetworkInvitationQuery from "users/networking/connections/invitations/_api/queries/network-invitation-query";
import { NetworkInvitationInfoJSON } from "users/networking/connections/invitations/_schemas/network-invitation-info-schema";

const InvitationContainer = styled("div")(({ theme }) => ({
  border: "1px solid #111",
  padding: theme.spacing(1),
}));
const MessageTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  marginTop: theme.spacing(1.5),
}));

export enum NetworkActionType {
  Invite = "invite",
  Accept = "accept",
  Reject = "reject",
  Cancel = "cancel",
}
export enum NetworkStatus {
  None = "none",
  Sent = "sent",
  Received = "received",
  InNetwork = "in-network",
  Self = "self",
  Rejected = "rejected",
}
export type NetworkAction = {
  invitation?: NetworkInvitationInfo;
  connection?: UserNetworkConnection;
  userId: Guid;
  action: NetworkActionType;
};

type NetworkButtonProps = {
  className?: string;
  variant?: "contained" | "outlined" | "text";
  userId: Guid;
};

export default function NetworkButton(props: Readonly<NetworkButtonProps>) {
  const { className, userId } = props;

  // #region Refs
  const menuButtonRef = React.useRef<MenuButtonRef>(null);

  // #region State
  const [message, setMessage] = React.useState<string>("");
  const [localInvitation, setLocalInvitation] = React.useState<NetworkInvitationInfoJSON | null>(null);

  React.useEffect(() => {
    setLocalInvitation(null);
  }, [userId]);

  // #region Hooks
  const confirm = useConfirmDialog();
  const { enqueueSnackbar } = useSnackbar();
  const session = useSession();

  // #region Queries
  const invitationQuery = React.useMemo(() => new NetworkInvitationQuery({}), []);
  const invitationsQuery = useGetUserNetworkInvitationsQuery(invitationQuery);
  const isNetworked = useSelector(selectIsNetworkedWith(userId));
  const networkConnection = useSelector(selectNetworkConnectionByUserId(userId));

  const networkInvitation = React.useMemo(() => {
    const queryInvitation = invitationsQuery.data?.data.find(
      (inv) => inv.invitee.id === userId.value || inv.inviter.id === userId.value
    );
    const json = queryInvitation ?? localInvitation;
    return json ? NetworkInvitationInfo.fromJSON(json) : undefined;
  }, [invitationsQuery.data, userId, localInvitation]);

  // #region Queries
  const [removeConnection, removeConnectionResult] = useRemoveNetworkConnectionMutation();
  const [createInvitation, createInvitationResult] = useCreateNetworkInvitationMutation();
  const [acceptInvitation, acceptInvitationResult] = useAcceptNetworkInvitationMutation();
  const [cancelInvitation, cancelInvitationResult] = useCancelNetworkInvitationMutation();
  const [rejectInvitation, rejectInvitationResult] = useRejectNetworkInvitationMutation();

  const buttonVariant = props.variant ?? "outlined";
  const status: NetworkStatus = getNetworkStatus(userId);
  const isSaving: boolean = createInvitationResult.isLoading
    || acceptInvitationResult.isLoading
    || cancelInvitationResult.isLoading
    || rejectInvitationResult.isLoading
    || removeConnectionResult.isLoading;

  function getNetworkStatus(userId: Guid): NetworkStatus {
    if (userId.isEqualTo(session.user?.id)) {
      return NetworkStatus.Self;
    }
    if (isNetworked) {
      return NetworkStatus.InNetwork;
    }
    if (networkInvitation) {
      if (networkInvitation.status === "rejected") return NetworkStatus.Rejected;
      if (networkInvitation.status === "open") {
        return networkInvitation.isInviter ? NetworkStatus.Sent : NetworkStatus.Received;
      }
    }
    return NetworkStatus.None;
  }

  async function handleNetworkButtonClicked(itemClicked: string) {
    if (status === NetworkStatus.Self) return;
    if (status === NetworkStatus.None) await sendInvitation();
    if (status === NetworkStatus.InNetwork) await removeNetworkConnection();
    if (status === NetworkStatus.Sent) await cancelNetworkInvitation();
    if (status === NetworkStatus.Rejected) return;
    if (status === NetworkStatus.Received) {
      if (itemClicked === "Accept Invitation") await acceptNetworkInvitation();
      if (itemClicked === "Reject Invitation") await rejectNetworkInvitation();
    }
  }

  async function sendInvitation() {
    try {
      const invitationData: NetworkInvitationAPIRequest = {
        recipientId: userId?.value,
        message: message
      };

      const result = await createInvitation(invitationData).unwrap();
      setLocalInvitation(result);

      enqueueSnackbar("Sent network invitation", { variant: "success" });
      menuButtonRef.current?.closeMenu();
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Couldn't send network request. Please try again.", {
        variant: "error",
      });
    }
  }

  async function removeNetworkConnection() {
    try {
      const response = await confirm({
        title: "Remove from network?",
        message: "Are you sure you want to remove this user from your network?",
        okButtonText: "Remove",
        cancelButtonText: "Cancel",
      });

      if (response == ConfirmResponse.Cancel) return;
      if (!networkConnection?.connectionId) throw new Error("Network connection id not found");

      await removeConnection({ connectionId: networkConnection.connectionId }).unwrap();

      enqueueSnackbar("Removed from network", { variant: "info" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Couldn't remove from network. Please try again.", {
        variant: "error",
      });
    }
  }

  async function cancelNetworkInvitation() {
    try {
      if (!networkInvitation) return;

      await cancelInvitation({ invitationId: networkInvitation.id }).unwrap();
      setLocalInvitation(null);

      enqueueSnackbar("Canceled network invitation", { variant: "info" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Couldn't cancel network request. Please try again.", {
        variant: "error",
      });
    }
  }

  async function acceptNetworkInvitation() {
    try {
      if (!networkInvitation) return;

      await acceptInvitation({ invitationId: networkInvitation.id }).unwrap();
      setLocalInvitation(null);
    } catch (error) {
      console.error(error);
      if (!(error instanceof Error)) return;
      enqueueSnackbar(
        error?.message?.length > 0
          ? error.message
          : "Couldn't accept network request. Please try again.",
        {
          variant: "error",
        }
      );
    }
  }

  async function rejectNetworkInvitation() {
    try {
      if (!networkInvitation) return;

      await rejectInvitation({ invitationId: networkInvitation.id }).unwrap();
      setLocalInvitation(null);

      enqueueSnackbar("Rejected network invitation", { variant: "info" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Couldn't reject network request. Please try again.", {
        variant: "error",
      });
    }
  }

  function getStartIcon() {
    switch (status) {
      case NetworkStatus.InNetwork:
        return <CloseIcon />;
      case NetworkStatus.Sent:
        return <NotInterestedIcon />;
      default:
        return <StarBorderIcon />;
    }
  }

  function getMenuOptions(): string[] {
    switch (status) {
      case NetworkStatus.None:
        return ["Add To Network"];
      case NetworkStatus.InNetwork:
        return ["Remove From Network"];
      case NetworkStatus.Sent:
        return ["Cancel Invitation"];
      case NetworkStatus.Received:
        return ["Accept Invitation", "Reject Invitation"];
      case NetworkStatus.Self:
        return ["You"];
      case NetworkStatus.Rejected:
        return ["Invitation Was Rejected"];
      default:
        return ["Add To Network"];
    }
  }

  function getPopoverContent(): React.ReactNode | undefined {
    if (status !== NetworkStatus.None) return undefined;

    return (
      <InvitationContainer>
        <Grid container direction="column">
          <Grid>
            <MessageTextField
              multiline
              variant="outlined"
              minRows={3}
              maxRows={6}
              fullWidth
              label="Message (optional)"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
            />
          </Grid>
          <Grid>
            <Button onClick={() => menuButtonRef.current?.closeMenu()}>
              Cancel
            </Button>
            <Button color="primary" onClick={sendInvitation}>
              Send Invitation
            </Button>
          </Grid>
        </Grid>
      </InvitationContainer>
    );
  }

  return (
    <MenuButton
      ref={menuButtonRef}
      className={className}
      variant={buttonVariant}
      color="primary"
      disabled={
        isSaving
        || status === NetworkStatus.Self
        || status === NetworkStatus.Rejected
      }
      saving={isSaving}
      startIcon={getStartIcon()}
      menuOptions={getMenuOptions()}
      popperContent={getPopoverContent()}
      onClick={handleNetworkButtonClicked}
    />
  );
}
